import React, { useState, useEffect } from 'react';
import { Mail, AlertCircle } from 'lucide-react';
import StepTracker from './StepTracker';
import { BACKEND_URL } from '../config';

const EmailValidation = ({ onValidationSuccess }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    let isInputFocused = false;

    const handleFocusIn = () => {
      isInputFocused = true;
    };

    const handleFocusOut = () => {
      if (isInputFocused) {
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          isInputFocused = false;
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const validateEmail = (email) => {
    return email.trim() !== '' && email.includes('@') && !/\s/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Digita un email de verdad -.-');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate',
          email: email.toLowerCase(),
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else if (data.skipToJoinDiscord) {
        const redirectUrl = `/join-discord?token=${data.token}`;
        window.location.href = redirectUrl;
      } else if (data.alreadyCompleted) {
        setError('Ya hiciste el onboarding');
        setCountdown(3);
        let count = 3;
        const interval = setInterval(() => {
          count--;
          setCountdown(count);
          if (count <= 0) {
            clearInterval(interval);
            window.location.href = 'https://www.nosoyliquidez.com';
          }
        }, 1000);
      } else if (data.canProceed) {
        onValidationSuccess(email.toLowerCase());
      }
    } catch {
      setError('Error al validar el correo. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4 py-8 overflow-x-hidden relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-700"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/logo-nsl.png" alt="NSL Logo" className="h-24 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
            Onboarding NSL
          </h1>
        </div>

        <StepTracker currentStep={1} />

        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-600/20 rounded-2xl shadow-2xl backdrop-blur-xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/10 rounded-full mb-4">
                <Mail className="w-8 h-8 text-yellow-500" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Ingresa tu correo electrónico</h2>
              <p className="text-gray-400 text-sm">Usaremos este correo para validar tu acceso</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
                  disabled={isLoading || countdown !== null}
                />
              </div>

              {error && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-500 text-sm">
                    {error}
                    {countdown !== null && ` Redirigiendo en ${countdown}...`}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || countdown !== null}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isLoading || countdown !== null
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black shadow-lg hover:shadow-yellow-500/50 transform hover:scale-105'
                }`}
              >
                {isLoading ? 'Validando...' : 'Continuar'}
              </button>
            </form>
          </div>

          <div className="text-center mt-8 text-gray-500 text-sm">
            <p>© {currentYear} No Soy Liquidez. Todos los derechos reservados.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailValidation;